/**
 * ChatInput — message input bar with text area, emoji toggle, GIF picker, and
 * send button. Supports reply preview and edit mode preview above the input.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { SendIcon, CloseIcon, SmileIcon } from '../../components/ui/icons';
import { GifPicker } from '../../components/ui/GifPicker';
import { EmojiPicker } from '../../components/ui/EmojiPicker';
import type { GifSelection } from '@wlb/shared/types/gif';
import type { ExtendedChatMessage, MessageReplyTo } from '@wlb/shared/types/chat';
import { getUsernameDisplay } from '../../utils/usernameDisplay';
import { useAutoResizeTextarea } from '../../utils/useAutoResizeTextarea';
import './messages.css';

/** Matches the `max-height` on `.chat-input` in messages.css. */
const CHAT_INPUT_MAX_HEIGHT = 120;

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  sending: boolean;
  /** Reply/edit are message-thread features; Robi omits them. */
  replyTo?: ExtendedChatMessage | null;
  editingMessage?: ExtendedChatMessage | null;
  onCancelReply?: () => void;
  onCancelEdit?: () => void;
  /** Called when the user picks a GIF from the picker. */
  onSendGif?: (gif: GifSelection) => void;
}

export function ChatInput({
  value,
  onChange,
  onSend,
  sending,
  replyTo = null,
  editingMessage = null,
  onCancelReply,
  onCancelEdit,
  onSendGif,
}: ChatInputProps) {
  const { t } = useTranslation();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useAutoResizeTextarea(inputRef, value, { maxHeight: CHAT_INPUT_MAX_HEIGHT });

  useEffect(() => {
    if (editingMessage) {
      inputRef.current?.focus();
    }
  }, [editingMessage]);

  useEffect(() => {
    if (replyTo) {
      inputRef.current?.focus();
    }
  }, [replyTo]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!value.trim() || sending) return;
    onSend();
    setShowEmojiPicker(false);
    setShowGifPicker(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  const toggleEmojiPicker = () => {
    setShowGifPicker(false);
    setShowEmojiPicker(prev => !prev);
  };

  const toggleGifPicker = () => {
    setShowEmojiPicker(false);
    setShowGifPicker(prev => !prev);
  };

  const handleGifSelect = (gif: GifSelection) => {
    setShowGifPicker(false);
    onSendGif?.(gif);
  };

  const replyPreview: MessageReplyTo | null = replyTo
    ? {
        _id: replyTo._id,
        text: replyTo.text,
        senderName: replyTo.user?.name,
        senderUsername: replyTo.user?.username,
      }
    : null;

  return (
    <div className="chat-input-bar">
      {replyPreview && !editingMessage && (
        <div className="chat-reply-preview">
          <div className="chat-reply-preview__body">
            <span className="chat-reply-preview__name">
              {t('chat.replyingTo', {
                name: getUsernameDisplay(
                  {
                    username: replyPreview.senderUsername,
                    name: replyPreview.senderName,
                  },
                  t('chat.unknownUser'),
                ),
              })}
            </span>
            <span className="chat-reply-preview__text">
              {replyPreview.text || ''}
            </span>
          </div>
          <button
            type="button"
            className="chat-reply-preview__close"
            onClick={onCancelReply}
            aria-label={t('common.buttons.cancel')}
          >
            <CloseIcon size={16} />
          </button>
        </div>
      )}

      {editingMessage && (
        <div className="chat-edit-preview">
          <div className="chat-edit-preview__body">
            <span className="chat-edit-preview__label">
              {t('individualChat.actions.edit')}
            </span>
            <span className="chat-edit-preview__text">
              {editingMessage.text}
            </span>
          </div>
          <button
            type="button"
            className="chat-edit-preview__close"
            onClick={onCancelEdit}
            aria-label={t('common.buttons.cancel')}
          >
            <CloseIcon size={16} />
          </button>
        </div>
      )}

      {showEmojiPicker && (
        <div className="chat-input__emoji-panel">
          <EmojiPicker
            onSelect={emoji => {
              onChange(value + emoji);
              inputRef.current?.focus();
            }}
          />
        </div>
      )}

      {showGifPicker && (
        <div className="chat-input__gif-panel">
          <GifPicker onSelect={handleGifSelect} disabled={sending} />
        </div>
      )}

      <form onSubmit={handleSubmit} className="chat-input-row">
        <div className={`chat-input-wrapper${editingMessage ? ' chat-input-wrapper--editing' : ''}`}>
          <textarea
            ref={inputRef}
            className="chat-input"
            value={value}
            onChange={event => onChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chat.inputPlaceholder')}
            aria-label={t('chat.inputPlaceholder')}
            rows={1}
          />
          <button
            type="button"
            className="chat-input-emoji-btn"
            onClick={toggleEmojiPicker}
            aria-label={t('comments.emojiPicker')}
            aria-pressed={showEmojiPicker}
          >
            <SmileIcon size={20} />
          </button>
          {onSendGif && (
            <button
              type="button"
              className="chat-input-gif-btn"
              onClick={toggleGifPicker}
              aria-label={t('comments.gifPicker')}
              aria-pressed={showGifPicker}
            >
              {t('individualChat.attachment.gif')}
            </button>
          )}
        </div>

        <button
          type="submit"
          className={`chat-input-send${sending ? ' chat-input-send--sending' : ''}`}
          disabled={!value.trim() || sending}
          aria-label={t('chat.send')}
        >
          <SendIcon size={20} />
        </button>
      </form>
    </div>
  );
}
